import type { DirectoryUser } from "@/lib/types";
import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  ActionConfirmationModal,
  Button,
  Card,
  CreatedByUserText,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Badge,
  Label,
  TextFormField,
  TextWithMentions,
  Typography,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useUser } from "@/components/UserProvider";
import {
  getUsersOptions,
  useCreateContactNote,
  useDeleteContactNote,
  useUpdateContactNote,
} from "@/lib/data/react-query";
import { type ContactNote, type ContactNoteComment } from "@/lib/types/Contact";
import { type User } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { NoteIcon } from "@/lib/utils/iconUtils";
import { buildMentionOptions } from "@/lib/utils/mentionUtils";
import { useQuery } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useWatch, type UseFormReturn } from "react-hook-form";
import { type z } from "zod";
import { ContactNoteComposer } from "./ContactNoteComposer";
import { ContactNoteComments } from "./ContactNoteComments";
import { FilePenLine, MoreVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import dayjs from "dayjs";

const newClientNoteId = (): string => {
  if (typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

function ContactNotesInlineFields({
  parentForm,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  className,
}: {
  parentForm: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  className?: string;
}) {
  const { control, register, setValue } = parentForm;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "notes",
  });
  const watchedNotes = useWatch({ control, name: "notes" });
  const noteCount = Math.max(fields.length, 1);
  const disabled = !hasEditPermission;

  const addRow = () => {
    if (!hasCreatePermission) {
      return;
    }

    append({
      id: newClientNoteId(),
      message: "",
      draft: false,
      dateModified: dayjs().toISOString(),
    });
  };

  const handleRemove = (index: number) => {
    if (!hasDeletePermission) {
      return;
    }
    remove(index);
  };

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {!hideLabel && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <NoteIcon className="text-muted-foreground h-5 w-5" />
            <Label className="text-lg font-semibold">
              {pluralize("Note", noteCount)}
            </Label>
          </div>
        </div>
      )}
      <div className="col-span-full">
        {fields.length > 0 && (
          <ul className="space-y-3">
            {fields.map((field, index) => {
              const row = watchedNotes?.[index];

              return (
                <li
                  key={field.id}
                  className="flex flex-col gap-1"
                  data-testid={
                    row?.id ? `contact-note-item-${row.id}` : undefined
                  }
                >
                  <div className="flex min-w-0 items-stretch gap-2 sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <TextFormField
                        className="w-full !space-y-0 [&>div:first-child]:hidden"
                        register={register(`notes.${index}.message`)}
                        placeholder="Add a note"
                        disabled={disabled}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-10 w-10 shrink-0",
                        row?.draft && "text-amber-500",
                      )}
                      disabled={disabled}
                      aria-label={
                        row?.draft
                          ? "Mark note as published"
                          : "Mark note as draft"
                      }
                      aria-pressed={row?.draft === true}
                      onClick={() => {
                        setValue(`notes.${index}.draft`, !row?.draft, {
                          shouldDirty: true,
                        });
                      }}
                    >
                      <FilePenLine className="h-4 w-4" />
                    </Button>
                    {hasDeletePermission && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        disabled={disabled}
                        onClick={() => {
                          handleRemove(index);
                        }}
                        aria-label="Remove note"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {hasCreatePermission && (
          <div className="mt-2 w-full self-start">
            <Button
              type="button"
              variant="link"
              className="contact-subitem-add text-muted-foreground h-auto min-h-0 justify-start p-0"
              onClick={addRow}
            >
              <Plus className="h-4 w-4 shrink-0" />
              Add note
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const ContactNoteMessage = ({
  text,
  users,
}: {
  text: string;
  users: DirectoryUser[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [text]);

  useEffect(() => {
    const element = textContentRef.current;
    if (!element) {
      setHasOverflow(false);
      return;
    }

    const determineOverflow = () => {
      if (isExpanded) {
        setHasOverflow(true);
        return;
      }
      const tolerancePx = 2;
      setHasOverflow(element.scrollHeight - element.clientHeight > tolerancePx);
    };

    determineOverflow();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!isExpanded) {
        determineOverflow();
      }
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [text, isExpanded]);

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={textContentRef}
        className={cn("text-sm", !isExpanded && "line-clamp-4")}
      >
        <TextWithMentions text={text} users={users} />
      </div>
      {hasOverflow && (
        <button
          type="button"
          className="text-primary w-fit text-xs font-medium hover:underline"
          onClick={() => {
            setIsExpanded((prev) => !prev);
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

function ContactNotesFeedSection({
  notes,
  comments,
  contactId,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  parentForm,
  className,
}: {
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  notes?: ContactNote[];
  comments?: ContactNoteComment[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  className?: string;
}) {
  const createContactNote = useCreateContactNote();
  const updateContactNote = useUpdateContactNote();
  const deleteContactNote = useDeleteContactNote();
  const { data: usersData, isLoading: isUsersLoading } =
    useQuery(getUsersOptions());
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [notePendingDelete, setNotePendingDelete] =
    useState<ContactNote | null>(null);

  const { currentUser } = useUser();
  const users = useMemo(() => usersData?.data ?? [], [usersData]);
  const mentionOptions = useMemo(() => buildMentionOptions(users), [users]);
  const pendingParentNoteIdRef = useRef(`pending-note-${newClientNoteId()}`);
  const pendingParentNoteId = pendingParentNoteIdRef.current;

  const parentFormNotes = parentForm?.watch("notes");
  const pendingParentNote = parentFormNotes?.find(
    (note) => note.id === pendingParentNoteId,
  );
  const notesToUse = useMemo(
    () =>
      (parentFormNotes
        ? parentFormNotes
            .filter((note) => note.id !== pendingParentNoteId)
            .map((note) => ({
              ...note,
              contactNoteCreator: currentUser.id,
            }))
        : notes) ?? [],
    [parentFormNotes, pendingParentNoteId, notes, currentUser.id],
  );

  const syncPendingParentNote = (message: string, isDraft: boolean) => {
    if (!parentForm) {
      return;
    }

    const existingNotes = parentForm.getValues("notes") ?? [];
    const pendingIndex = existingNotes.findIndex(
      (note) => note.id === pendingParentNoteId,
    );
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      if (pendingIndex === -1) {
        return;
      }
      parentForm.setValue(
        "notes",
        existingNotes.filter((note) => note.id !== pendingParentNoteId),
        { shouldDirty: true },
      );
      return;
    }

    const nextPendingNote = {
      id: pendingParentNoteId,
      message: message,
      draft: isDraft,
      dateModified: dayjs().toISOString(),
    };

    parentForm.setValue(
      "notes",
      pendingIndex === -1
        ? [...existingNotes, nextPendingNote]
        : existingNotes.map((note) =>
            note.id === pendingParentNoteId ? nextPendingNote : note,
          ),
      { shouldDirty: true },
    );
  };

  const notesWithMeta = useMemo(() => {
    const sortedNotes = [...notesToUse].sort((a, b) =>
      dayjs(b.dateModified).diff(dayjs(a.dateModified)),
    );

    return sortedNotes.map((note) => {
      const noteAuthor =
        "author" in note && note.author ? note.author : undefined;
      const noteAuthorId =
        noteAuthor?.id ??
        ("authorUserId" in note ? note.authorUserId : undefined) ??
        ("contactNoteCreator" in note ? note.contactNoteCreator : undefined);
      const hasInlineAuthor = Boolean(
        noteAuthor?.firstName && noteAuthor.lastName,
      );
      const createdByUser = hasInlineAuthor
        ? {
            id: noteAuthorId ?? "",
            firstName: noteAuthor?.firstName ?? "",
            lastName: noteAuthor?.lastName ?? "",
            profilePicturePresignedUrlKey: undefined,
          }
        : users.find((user) => user.id === noteAuthorId);
      return {
        ...note,
        createdByUser,
        createdByName: formatUsersName(createdByUser) || "-",
      } as ContactNote & {
        createdByUser?: Pick<
          User,
          "id" | "firstName" | "lastName" | "profilePicturePresignedUrlKey"
        >;
        createdByName: string;
      };
    });
  }, [notesToUse, users]);

  const notesCount = notesWithMeta.length;
  const draftNotes = useMemo(
    () => notesWithMeta.filter((note) => note.draft),
    [notesWithMeta],
  );
  const publishedNotes = useMemo(
    () => notesWithMeta.filter((note) => !note.draft),
    [notesWithMeta],
  );

  const allowCreate = Boolean(parentForm) || !!hasCreatePermission;
  const allowEdit = Boolean(parentForm) || !!hasEditPermission;
  const allowDelete = Boolean(parentForm) || !!hasDeletePermission;

  const handleCreateNote = async (message: string, isDraft: boolean) => {
    if (parentForm) {
      const existing = parentForm.getValues("notes") ?? [];
      const nextExisting = existing.filter(
        (note) => note.id !== pendingParentNoteId,
      );
      parentForm.setValue(
        "notes",
        [
          ...nextExisting,
          {
            id: newClientNoteId(),
            message: message,
            draft: isDraft,
            dateModified: dayjs().toISOString(),
          },
        ],
        { shouldDirty: true },
      );
      return;
    }
    await createContactNote.mutateAsync({
      contactId,
      message: message,
      draft: isDraft,
    });
  };

  const handleUpdateNote = async (
    note: ContactNote,
    message: string,
    isDraft: boolean,
  ) => {
    if (parentForm) {
      const existingNotes = parentForm.getValues("notes") ?? [];
      parentForm.setValue(
        "notes",
        existingNotes.map((current) =>
          current.id === note.id
            ? {
                ...current,
                message: message,
                draft: isDraft,
              }
            : current,
        ),
        { shouldDirty: true },
      );
      setEditingNoteId(null);
      return;
    }
    const nextDraft = Boolean(isDraft);
    const prevDraft = Boolean(note.draft);
    const dirtyPayload: Partial<ContactNote> = {};
    if (message !== note.message) {
      dirtyPayload.message = message;
    }
    if (nextDraft !== prevDraft) {
      dirtyPayload.draft = nextDraft;
    }
    if (Object.keys(dirtyPayload).length === 0) {
      setEditingNoteId(null);
      return;
    }
    await updateContactNote.mutateAsync({
      contactId,
      id: note.id,
      dateModified: note.dateModified,
      ...dirtyPayload,
    });
    setEditingNoteId(null);
  };

  const handleAutoSaveDraft = (message: string) => {
    if (parentForm) {
      return;
    }
    createContactNote.mutate({
      contactId,
      message: message,
      draft: true,
    });
  };

  const handleDeleteNote = async (note: ContactNote) => {
    if (parentForm) {
      const existingNotes = parentForm.getValues("notes") ?? [];
      parentForm.setValue(
        "notes",
        existingNotes.filter((current) => current.id !== note.id),
        { shouldDirty: true },
      );
    } else {
      await deleteContactNote.mutateAsync({
        id: note.id,
        contactId,
      });
    }
    if (editingNoteId === note.id) {
      setEditingNoteId(null);
    }
    setNotePendingDelete(null);
  };

  const renderNotesList = (notesToRender: typeof notesWithMeta) => (
    <div className="grid gap-3">
      {notesToRender.map((note) => {
        const showActions = allowEdit || allowDelete;
        const isEditingThisNote = editingNoteId === note.id;

        return (
          <Card key={note.id} className="border-border relative border p-4">
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              {isEditingThisNote ? (
                <ContactNoteComposer
                  mentionOptions={mentionOptions}
                  isMentionLoading={isUsersLoading}
                  initialContent={note.message ?? ""}
                  initialDraft={note.draft ?? false}
                  showDraftToggle={note.draft ?? false}
                  onSubmit={async (message, isDraft) => {
                    await handleUpdateNote(
                      note,
                      message,
                      note.draft ? isDraft : false,
                    );
                  }}
                  onCancel={() => {
                    setEditingNoteId(null);
                  }}
                  submitting={updateContactNote.isPending}
                  primaryButtonLabel="Save changes"
                />
              ) : (
                <ContactNoteMessage text={note.message ?? ""} users={users} />
              )}
              {note.draft && (
                <Badge variant="secondary" className="h-6 w-fit text-xs">
                  Draft
                </Badge>
              )}
              <CreatedByUserText
                user={note.createdByUser}
                dateTime={note.publishedDate ?? note.dateCreated}
                showTime
              />
              {!parentForm && (
                <ContactNoteComments
                  contactId={contactId}
                  noteId={note.id}
                  comments={comments}
                  users={users}
                  mentionOptions={mentionOptions}
                  isUsersLoading={isUsersLoading}
                  currentUser={currentUser}
                  canCreateComment={allowCreate}
                  canEditComment={allowEdit}
                  canDeleteComment={allowDelete}
                />
              )}
            </div>
            {showActions && !isEditingThisNote && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8"
                    data-testid="contact-note-actions-trigger"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {allowEdit && (
                    <DropdownMenuItem
                      data-testid="contact-note-edit-action"
                      onClick={() => {
                        setEditingNoteId(note.id);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" /> Edit
                    </DropdownMenuItem>
                  )}
                  {allowDelete && (
                    <DropdownMenuItem
                      data-testid="contact-note-delete-action"
                      onSelect={() => {
                        window.setTimeout(() => {
                          setNotePendingDelete(note);
                        }, 0);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </Card>
        );
      })}
    </div>
  );

  return (
    <>
      <div className={cn("flex h-full min-h-0 flex-col gap-4", className)}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {!hideLabel && (
            <div className="flex items-center gap-2">
              <NoteIcon className="text-muted-foreground h-5 w-5" />
              <div className="flex items-baseline gap-2">
                <Label className="text-lg font-semibold">Notes</Label>
                <Typography
                  size="sm"
                  variant="subHeading"
                  className="text-muted-foreground"
                >
                  {pluralize("note", notesCount, true)}
                </Typography>
              </div>
            </div>
          )}
        </div>

        {allowCreate && (
          <div className="flex-shrink-0">
            <ContactNoteComposer
              mentionOptions={mentionOptions}
              isMentionLoading={isUsersLoading}
              initialContent={pendingParentNote?.message ?? undefined}
              initialDraft={pendingParentNote?.draft ?? undefined}
              onSubmit={handleCreateNote}
              onChange={parentForm ? syncPendingParentNote : undefined}
              onAutoSaveDraft={handleAutoSaveDraft}
              autoSaveOnUnmount={!parentForm}
              submitting={parentForm ? false : createContactNote.isPending}
              primaryButtonLabel="Add note"
              showPrimaryButton
            />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {draftNotes.length > 0 && (
            <Accordion type="single" collapsible className="mb-3">
              <AccordionItem value="drafts">
                <AccordionTrigger>
                  <div className="flex items-baseline gap-2">
                    <Typography size="sm" variant="subHeading">
                      Drafts
                    </Typography>
                    <Badge variant="secondary" className="h-5 px-2 text-xs">
                      {draftNotes.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2">
                  {renderNotesList(draftNotes)}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {publishedNotes.length > 0 ? (
            renderNotesList(publishedNotes)
          ) : draftNotes.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4 text-center">
              <Typography
                variant="subHeading"
                className="text-muted-foreground italic"
              >
                No notes found.
              </Typography>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center px-4 text-center">
              <Typography
                variant="subHeading"
                className="text-muted-foreground italic"
              >
                No published notes.
              </Typography>
            </div>
          )}
        </div>
      </div>
      <ActionConfirmationModal
        title="Are you sure you want to delete this note?"
        open={Boolean(notePendingDelete)}
        setOpen={(open) => {
          if (!open) {
            setNotePendingDelete(null);
          }
        }}
        afterActionFn={() => {
          if (notePendingDelete) {
            void handleDeleteNote(notePendingDelete);
          }
        }}
      />
    </>
  );
}

export default function ContactNotesSection({
  notes,
  comments,
  contactId,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  parentForm,
  className,
}: {
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  notes?: ContactNote[];
  comments?: ContactNoteComment[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  className?: string;
}) {
  if (parentForm) {
    return (
      <ContactNotesInlineFields
        parentForm={parentForm}
        hasCreatePermission={hasCreatePermission}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hideLabel={hideLabel}
        className={className}
      />
    );
  }

  return (
    <ContactNotesFeedSection
      notes={notes}
      comments={comments}
      contactId={contactId}
      hasCreatePermission={hasCreatePermission}
      hasEditPermission={hasEditPermission}
      hasDeletePermission={hasDeletePermission}
      hideLabel={hideLabel}
      parentForm={parentForm}
      className={className}
    />
  );
}
